import type {
  AutomationClassInfo,
  AutomationClassNotesDto,
  AutomationFreshAccount,
} from '@tms/shared';
import type { PoolAccountSummary, ReadinessMatrix, RegistrationAccount } from '../testrun-types';
import { segmentOfClass } from './gradle';

/**
 * The rules of the run pre-flight (TestRun src/components/RunPreflight.tsx):
 * which pool account a class goes under, whether it fits the class's
 * `requires`, and the automatic picks — for classes the given account does not
 * fit, for one-off («new account every run») classes, for rotation and for
 * registration. Pure functions: the dialog renders, these decide.
 */

export type ClassInfo = AutomationClassInfo;

/** Per-class accounts set in this run: class → the fields that differ from the scan. */
export type Overrides = Record<string, { phone?: string; iin?: string; bin?: string }>;

/** A pool or registration account, as far as a class needs it. */
export interface AccountLike {
  id: string;
  phone: string;
  iin: string;
  bin?: string | null;
}

/** Pool account types are TestRun's own values. */
const IE = 'ИП';
const LLP = 'ТОО';

/**
 * A note like «needs a new account» — the same sign the scanner takes from
 * testdata and code; people write it by hand for classes the scanner does not know.
 */
const FRESH_TEXT_RE = /(нов|свеж)\p{L}*\s+аккаунт|\b(new|fresh)\s+account\b/iu;

/** The prefix a scanner comment repeats after the «new account every run» heading. */
export const FRESH_REASON_PREFIX_RE =
  /^\s*нов\p{L}*\s+аккаунт\s+на\s+каждый\s+прогон\s*[:.—-]?\s*/iu;

/** Needs a new account on every run: the scanner's sign, or a class note saying so. */
export const freshOf = (
  c: ClassInfo,
  notes: AutomationClassNotesDto | undefined,
): AutomationFreshAccount | null => {
  if (c.freshAccount) return c.freshAccount;
  const note = notes?.[c.class]?.text;
  return note && FRESH_TEXT_RE.test(note) ? { sources: ['note'], cases: null, reason: note } : null;
};

const digits = (value?: string | null) => (value ?? '').replace(/\D/g, '').slice(-10);

/** The pool account a class goes under, by phone. */
export const findInPool = (
  accounts: PoolAccountSummary[] | undefined,
  phone: string,
): PoolAccountSummary | undefined =>
  phone ? accounts?.find((a) => digits(a.phone) === digits(phone)) : undefined;

/** The «for registration» account a class goes under, by phone and IIN. */
export const findReg = (
  accounts: RegistrationAccount[] | undefined,
  phone: string,
  iin: string,
): RegistrationAccount | undefined =>
  phone
    ? accounts?.find((a) => digits(a.phone) === digits(phone) && a.iin === iin.replace(/\D/g, ''))
    : undefined;

/* ─── verdicts: will a class go on a pool account ─── */

export type VerdictKind = 'ok' | 'missing' | 'broken' | 'unknown';
/** Why: the readiness cell's status, so the page can say it in words. */
export type VerdictReason =
  'ok' | 'type' | 'spent' | 'missing' | 'broken' | 'notChecked' | 'unchecked';

/** broken — the stand did not return data the class needs: the test fails as on «no» */
export interface Verdict {
  kind: VerdictKind;
  reason: VerdictReason;
  /** the capabilities' short names, or the cell's own detail */
  detail?: string;
}

export const unfit = (v: Verdict | null | undefined): boolean =>
  v?.kind === 'missing' || v?.kind === 'broken';

/** By the «ready for tests» matrix. null — the class has no requirements, or there is no data. */
export const verdictFor = (
  m: ReadinessMatrix | null | undefined,
  className: string,
  a: PoolAccountSummary | undefined,
): Verdict | null => {
  const cls = m?.classes.find((c) => c.class === className);
  if (!m || !cls || !a || (!cls.requires.length && !cls.type && !cls.freshAccount)) return null;
  const cell = cls.cells[a.id];
  if (!cell) return null;
  const short = (keys: string[] = []) =>
    keys.map((k) => m.capabilities.find((c) => c.key === k)?.short ?? k).join(', ');
  if (cell.status === 'ok') return { kind: 'ok', reason: 'ok' };
  if (cell.status === 'type')
    return { kind: 'missing', reason: 'type', ...(cell.detail ? { detail: cell.detail } : {}) };
  if (cell.status === 'spent')
    return { kind: 'missing', reason: 'spent', detail: cell.detail ?? '' };
  if (cell.status === 'missing')
    return { kind: 'missing', reason: 'missing', detail: short(cell.missing) };
  if (cell.broken?.length) return { kind: 'broken', reason: 'broken', detail: short(cell.broken) };
  return cell.unknown?.length
    ? { kind: 'unknown', reason: 'notChecked', detail: short(cell.unknown) }
    : { kind: 'unknown', reason: 'unchecked' };
};

/** A group of classes on one account: fits all / what is missing, and how many it fits. */
export interface GroupVerdict {
  kind: VerdictKind;
  /** the unfit verdicts (kind missing) or the first unknown one */
  verdicts: Verdict[];
  fit: number;
  of: number;
}

export const groupVerdict = (
  m: ReadinessMatrix | null | undefined,
  classes: ClassInfo[],
  a: PoolAccountSummary,
): GroupVerdict | null => {
  const vs = classes.map((c) => verdictFor(m, c.class, a)).filter((v): v is Verdict => v !== null);
  if (!vs.length) return null;
  const bad = vs.filter((v) => unfit(v));
  const fit = vs.filter((v) => v.kind === 'ok').length;
  // classes of a group can have different requires: «fits 8 of 10» — the best partial pick on top
  if (bad.length) return { kind: 'missing', verdicts: bad, fit, of: vs.length };
  const unknown = vs.find((v) => v.kind === 'unknown');
  if (unknown) return { kind: 'unknown', verdicts: [unknown], fit, of: vs.length };
  return { kind: 'ok', verdicts: [], fit, of: vs.length };
};

/* ─── setting an account ─── */

/** A class goes under a pool account; fields equal to the scanned ones are not overridden. */
export const applyAccount = (prev: Overrides, c: ClassInfo, a: AccountLike): Overrides => {
  const cur: Overrides[string] = {};
  if (a.phone !== c.account.phone) cur.phone = a.phone;
  if (a.iin !== c.account.iin) cur.iin = a.iin;
  if (a.bin && a.bin !== c.account.bin) cur.bin = a.bin;
  const next = { ...prev };
  if (!cur.phone && !cur.iin && !cur.bin) delete next[c.class];
  else next[c.class] = cur;
  return next;
};

/** One field of some classes: a value equal to the scanned one, or empty, drops the override. */
export const setField = (
  prev: Overrides,
  classes: ClassInfo[],
  field: 'phone' | 'iin' | 'bin',
  value: string,
): Overrides => {
  const next = { ...prev };
  for (const c of classes) {
    const orig = c.account[field];
    const cur = { ...next[c.class] };
    if (value === orig || value === '') delete cur[field];
    else cur[field] = value;
    if (!cur.phone && !cur.iin && !cur.bin) delete next[c.class];
    else next[c.class] = cur;
  }
  return next;
};

/* ─── automatic picks ─── */

const checkedAt = (a: PoolAccountSummary) => (a.checkedAt ? Date.parse(a.checkedAt) : 0);

interface Picks<T> {
  picks: Record<string, T>;
  kept: string[];
}

/**
 * A pool account for classes with `requires` that the given account does not
 * fit. Classes without requires are left alone: their testdata is tied to
 * their own account (a loan id, a card). Among fitting accounts: one already
 * chosen in this run, then the one that fits more classes, then the freshest
 * check — fewer accounts mean fewer logins, and the stand limits them.
 */
export const autoPick = (
  classes: ClassInfo[],
  pool: PoolAccountSummary[],
  m: ReadinessMatrix,
  phoneOf: (c: ClassInfo) => string,
): Picks<PoolAccountSummary> => {
  const requiresOf = (c: ClassInfo) => m.classes.find((x) => x.class === c.class)?.requires ?? [];
  const fits = (c: ClassInfo, a: PoolAccountSummary) => verdictFor(m, c.class, a)?.kind === 'ok';
  // Only when it is known not to fit: the account is in the pool and the check says «no: …».
  // An account outside the pool is not touched — class requirements are still incomplete, and
  // a bolder pick on 13.10 moved loans and LLP QR off working accounts into the red.
  const need = classes.filter((c) => {
    if (!requiresOf(c).length) return false;
    const cur = findInPool(pool, phoneOf(c));
    return Boolean(cur) && unfit(verdictFor(m, c.class, cur));
  });
  const score = new Map(pool.map((a) => [a.id, need.filter((c) => fits(c, a)).length]));
  const chosen = new Set<string>();
  const picks: Record<string, PoolAccountSummary> = {};
  const kept: string[] = [];
  for (const c of need) {
    const cands = pool.filter((a) => fits(c, a));
    const pick = [...cands].sort(
      (x, y) =>
        Number(chosen.has(y.id)) - Number(chosen.has(x.id)) ||
        (score.get(y.id) ?? 0) - (score.get(x.id) ?? 0) ||
        checkedAt(y) - checkedAt(x),
    )[0];
    if (!pick) {
      kept.push(c.class);
      continue;
    }
    picks[c.class] = pick;
    chosen.add(pick.id);
  }
  return { picks, kept };
};

/**
 * Rotation (testdata rotateAccount — logins, OTP): a pool account of the
 * class's type that has gone longest without a run, checked, without a login
 * error and not taken by other classes of the run. Per-IIN limits (biometry
 * 429, OTP cooldown) spread over the pool instead of piling up on one account.
 */
export const pickRotate = (
  classes: ClassInfo[],
  pool: PoolAccountSummary[],
  m: ReadinessMatrix,
  taken: Set<string>,
): Picks<PoolAccountSummary> => {
  const used = new Set(taken);
  const picks: Record<string, PoolAccountSummary> = {};
  const kept: string[] = [];
  const last = (a: PoolAccountSummary) => (a.lastUsed?.at ? Date.parse(a.lastUsed.at) : 0);
  for (const c of classes) {
    const seg = segmentOfClass(c);
    const type = seg === 'IP' ? IE : seg === 'TOO' ? LLP : c.account.bin ? LLP : IE;
    const pick = pool
      .filter((a) => {
        if (used.has(a.id) || a.type !== type || !a.capabilities || a.lastError) return false;
        const v = verdictFor(m, c.class, a);
        return !v || v.kind === 'ok';
      })
      .sort(
        (x, y) => last(x) - last(y) || String(y.checkedAt).localeCompare(String(x.checkedAt)),
      )[0];
    if (!pick) {
      kept.push(c.class);
      continue;
    }
    picks[c.class] = pick;
    used.add(pick.id);
  }
  return { picks, kept };
};

/**
 * One account for the whole run (testrun.singleAccount, smoke): it changes on
 * every class at once, and only when the current one is in the pool and surely
 * does not fit one of the classes. The new one is of the same type and fits all.
 * null — nothing to change; { pick: undefined } — none fits all, the given one stays.
 */
export const pickSingle = (
  classes: ClassInfo[],
  pool: PoolAccountSummary[],
  m: ReadinessMatrix,
  phoneOf: (c: ClassInfo) => string,
): { pick: PoolAccountSummary | undefined } | null => {
  const first = classes[0];
  if (!first) return null;
  const cur = findInPool(pool, phoneOf(first));
  if (!cur || !classes.some((c) => unfit(verdictFor(m, c.class, cur)))) return null;
  const fitsAll = (a: PoolAccountSummary) =>
    classes.every((c) => {
      const v = verdictFor(m, c.class, a);
      return !v || v.kind === 'ok';
    });
  const pick = pool
    .filter((a) => a.type === cur.type && a.capabilities && fitsAll(a))
    .sort((x, y) => checkedAt(y) - checkedAt(x))[0];
  return { pick };
};

/**
 * «A new account every run»: each such class gets a pool account it was never
 * given (the mark is set when the run starts), checked, of its type, fitting
 * its requires and not taken by other classes of the run. Accounts untouched
 * by one-off classes go first, then the freshest checks.
 */
export const pickFresh = (
  classes: ClassInfo[],
  pool: PoolAccountSummary[],
  m: ReadinessMatrix,
  taken: Set<string>,
): Picks<PoolAccountSummary> => {
  const used = new Set(taken);
  const picks: Record<string, PoolAccountSummary> = {};
  const kept: string[] = [];
  for (const c of classes) {
    const seg = segmentOfClass(c);
    const type = seg === 'IP' ? IE : seg === 'TOO' ? LLP : null;
    const pick = pool
      .filter((a) => {
        if (used.has(a.id) || !a.capabilities || (a.spent ?? []).some((s) => s.class === c.class)) {
          return false;
        }
        if (type && a.type !== type) return false;
        const v = verdictFor(m, c.class, a);
        return v ? v.kind === 'ok' : true;
      })
      .sort(
        (x, y) => (x.spent?.length ?? 0) - (y.spent?.length ?? 0) || checkedAt(y) - checkedAt(x),
      )[0];
    if (!pick) {
      kept.push(c.class);
      continue;
    }
    picks[c.class] = pick;
    used.add(pick.id);
  }
  return { picks, kept };
};

/** Registration classes get a free account of their kind (IP / TOO / MF): least issued first, then the oldest. */
export const pickRegistration = (
  classes: ClassInfo[],
  list: RegistrationAccount[],
): Picks<RegistrationAccount> => {
  const used = new Set<string>();
  const picks: Record<string, RegistrationAccount> = {};
  const kept: string[] = [];
  for (const c of classes) {
    const cand = list
      .filter((a) => a.status === 'free' && a.kind === c.registration && !used.has(a.id))
      .sort((x, y) => x.attempts - y.attempts || x.addedAt.localeCompare(y.addedAt))[0];
    if (!cand) {
      kept.push(c.class);
      continue;
    }
    picks[c.class] = cand;
    used.add(cand.id);
  }
  return { picks, kept };
};

/** What the automatic pick did when the dialog opened. */
export interface AutoPickState {
  /** class → the id of the account given to it */
  picked: Record<string, string>;
  /** no fitting account in the pool: the given one stays */
  kept: string[];
  /** one-off classes that got an unused pool account */
  fresh: string[];
  /** one-off classes with no free pool account */
  freshKept: string[];
  /** rotateAccount classes that got the longest-unused account */
  rotate: string[];
  /** registration classes that got a «for registration» account */
  reg: string[];
  /** registration classes with no free account of their kind */
  regKept: string[];
}
